import { Koi } from '@ksyos/koi';
import type { IComorbidities } from '@common/interfaces/comorbidities';
import type { IPatientContactDetails } from '@common/interfaces/patientContactDetails';
import type { ProcessActivityProfessionalSummaryData } from '@common/validation/customControls/processActivityProfessionalSummaryData';
import type { ProcessActivityAsset } from '@common/validation/processActivityAsset';
import type { TypedSchema } from '@common/validation/utils/typedSchemaType';
import { Retrognathism, TongueSize, TonsilSize, YesOrNoOrUnknown } from '@common/enums';
import { ComorbiditiesChoice } from '@common/enums/comorbiditiesChoice';
import { addressValidationDraftSchema, addressValidationFinalSchema } from '@common/validation/address';
import { processActivityProfessionalSummaryDataSchema } from '@common/validation/customControls/processActivityProfessionalSummaryData';
import { getProcessActivityAssetsSchema } from '@common/validation/processActivityAsset';
import {
    emailSchema,
    gewichtInKgSchema,
    lengteInCmSchema,
    onePhoneNumberRequiredSchema,
} from '@common/validation/schemas';
import {
    identifierSocialSecuritySchemaDraft,
    identifierSocialSecuritySchemaFinal,
} from '@common/validation/schemas/identifierSocialSecuritySchema';
import { getTextAreaStringSchema } from '@common/validation/utils/getTextAreaStringSchema';
import type { ProcessActivityMedicationData } from '../../processActivityMedicationData';
import type { IntoxicationCustomControlData } from '../intoxicationSchemas';
import {
    processActivityMedicationDataSchema,
    processActivityMedicationDraftDataSchema,
} from '../../processActivityMedicationData';
import { intoxicationSchema } from '../intoxicationSchemas';
import { SleepRecorderType } from '../sleepRecorderType';

export const osasReferralComorbiditiesLabels: { [key: string]: string } = {
    COPD: 'COPD',
    burnoutOrDepressed: 'Burn-out of depressieve klachten',
    cardialProblems: 'Cardiale problematiek',
    diabetesMellitus: 'Diabetes mellitus',
    highBloodPressure: 'Hoge bloeddruk',
};

export const osasReferralImportantComplaintsLabels: { [key: string]: string } = {
    breathStops: 'Ademstops',
    sleepiness: 'Slaperigheid',
    snoring: 'Snurken',
    tiredness: 'Vermoeidheid',
};

export type OsasReferralCustomControlData = {
    assets: ProcessActivityAsset[];
    comorbidities: IComorbidities;
    comorbidityChoice: ComorbiditiesChoice | null;
    draftCareProviderOrganizationId?: string | null;
    draftCareProviderPractitionerId?: string | null;
    hasDentures: YesOrNoOrUnknown | null;
    importantComplaints: {
        breathStops: boolean;
        other: string | null;
        sleepiness: boolean;
        snoring: boolean;
        tiredness: boolean;
    };
    isDentitionWellSanitized: YesOrNoOrUnknown | null;
    isDentitionWellSanitizedRemark: string | null;
    isPatientInformed: boolean;
    lengthInCm: number | null;
    patient: IPatientContactDetails & { identifierSocialSecurity: string | null };
    referralReason: string | null;
    retrognathism: Retrognathism | null;
    sleepRecorderType: SleepRecorderType | null;
    tongueSize: TongueSize | null;
    tonsilSize: TonsilSize | null;
    weightInKg: number | null;
};

export type IOsasReferralData = OsasReferralCustomControlData &
    ProcessActivityProfessionalSummaryData &
    ProcessActivityMedicationData &
    IntoxicationCustomControlData;

type TypedOsasReferralSchema = TypedSchema<OsasReferralCustomControlData>;

export const comorbiditiesSchema = Koi.object({
    COPD: Koi.boolean(),
    burnoutOrDepressed: Koi.boolean(),
    cardialProblems: Koi.boolean(),
    diabetesMellitus: Koi.boolean(),
    highBloodPressure: Koi.boolean(),
    other: getTextAreaStringSchema(true, 255),
});

const osasReferralDraft: TypedOsasReferralSchema = {
    assets: getProcessActivityAssetsSchema({ max: 20 }),
    comorbidities: comorbiditiesSchema,
    comorbidityChoice: Koi.koi().enum(ComorbiditiesChoice).allow(null),
    draftCareProviderOrganizationId: Koi.string().uuid().allow(null),
    draftCareProviderPractitionerId: Koi.string().uuid().allow(null),
    hasDentures: Koi.koi().enum(YesOrNoOrUnknown).allow(null),
    importantComplaints: Koi.object({
        breathStops: Koi.boolean(),
        other: getTextAreaStringSchema(true, 255),
        sleepiness: Koi.boolean(),
        snoring: Koi.boolean(),
        tiredness: Koi.boolean(),
    }),
    isDentitionWellSanitized: Koi.koi().enum(YesOrNoOrUnknown).allow(null),
    isDentitionWellSanitizedRemark: getTextAreaStringSchema(true),
    isPatientInformed: Koi.boolean().optional(),
    lengthInCm: lengteInCmSchema.allow(null),
    patient: Koi.object({
        emailAddress: emailSchema.allow(null),
        homePhoneNumber: getTextAreaStringSchema(true, 255),
        identifierSocialSecurity: identifierSocialSecuritySchemaDraft,
        mobileNumber: getTextAreaStringSchema(true, 255),
    }).concat(addressValidationDraftSchema),
    referralReason: getTextAreaStringSchema(true, 255),
    retrognathism: Koi.koi().enum(Retrognathism).allow(null),
    sleepRecorderType: Koi.koi().enum(SleepRecorderType).allow(null),
    tongueSize: Koi.koi().enum(TongueSize).allow(null),
    tonsilSize: Koi.koi().enum(TonsilSize).allow(null),
    weightInKg: gewichtInKgSchema.allow(null),
};

const osasReferralFinal: TypedOsasReferralSchema = {
    assets: getProcessActivityAssetsSchema({ max: 20 }),
    comorbidities: Koi.when('comorbidityChoice', {
        is: ComorbiditiesChoice.ja,
        otherwise: comorbiditiesSchema,
        then: comorbiditiesSchema.anyFilledIn(),
    }),
    comorbidityChoice: Koi.koi().enum(ComorbiditiesChoice),
    draftCareProviderOrganizationId: Koi.string().uuid().required(),
    draftCareProviderPractitionerId: Koi.string().uuid().required(),
    hasDentures: Koi.koi().enum(YesOrNoOrUnknown).allow(null),
    importantComplaints: Koi.object({
        breathStops: Koi.boolean(),
        other: getTextAreaStringSchema(true, 255),
        sleepiness: Koi.boolean(),
        snoring: Koi.boolean(),
        tiredness: Koi.boolean(),
    }).anyFilledIn(),
    isDentitionWellSanitized: Koi.koi().enum(YesOrNoOrUnknown).allow(null),
    isDentitionWellSanitizedRemark: getTextAreaStringSchema(true),
    isPatientInformed: Koi.boolean(),
    lengthInCm: lengteInCmSchema,
    patient: Koi.object({
        emailAddress: emailSchema,
        identifierSocialSecurity: identifierSocialSecuritySchemaFinal,
    })
        .concat(addressValidationFinalSchema)
        .concat(onePhoneNumberRequiredSchema('homePhoneNumber', 'mobileNumber')),
    referralReason: getTextAreaStringSchema(false, 255),
    retrognathism: Koi.koi().enum(Retrognathism).allow(null),
    sleepRecorderType: Koi.koi().enum(SleepRecorderType),
    tongueSize: Koi.koi().enum(TongueSize).allow(null),
    tonsilSize: Koi.koi().enum(TonsilSize).allow(null),
    weightInKg: gewichtInKgSchema,
};

export const osasReferralFinalSchema = Koi.object(osasReferralFinal)
    .concat(processActivityProfessionalSummaryDataSchema)
    .concat(processActivityMedicationDataSchema)
    .concat(intoxicationSchema);
export const osasReferralDraftSchema = Koi.object(osasReferralDraft)
    .concat(processActivityProfessionalSummaryDataSchema)
    .concat(processActivityMedicationDraftDataSchema)
    .concat(intoxicationSchema);
